import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import styled from 'styled-components'
import { Text, Card } from '../../../components/BaseComponents'
import { Colors } from '../../../styles/theme'
import { Image } from 'semantic-ui-react'

const MainContainer = styled.div`
  max-width: 72rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding-top: 2rem;
`

const WelcomeImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e4f2e6;
  border-radius: 16px;
  padding: 24px;
`

const WelcomeSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`

const WelcomeTextContainer = styled.div`
  max-width: 32rem;
  margin: 0 auto;
`

const UpNextSection = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledCard = styled(Card)`
  background: linear-gradient(
    180deg,
    rgba(141, 198, 210, 0.35) 0%,
    rgba(232, 239, 230, 0) 100%
  ) !important;
  box-shadow: none !important;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  @media (min-width: 1024px) {
    align-items: flex-start;
  }
`

const ImageContainer = styled.div`
  margin-bottom: 1.5rem;
`

const StyledImage = styled(Image)`
  width: 200px;
  height: auto;
`

const TextSection = styled.div`
  text-align: center;

  @media (min-width: 1024px) {
    text-align: left;
  }
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const EyebrowText = styled(Text)`
  color: ${Colors.charcoal};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
`

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StepCard = styled(Card)`
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const StepHeader = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`

const StepNumber = styled.div<{ isActive?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.isActive ? Colors.orange : Colors.lightGray};
  border-radius: 50%;
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
`

const StepNumberText = styled(Text)<{ isActive?: boolean }>`
  color: ${(props) => (props.isActive ? 'white' : Colors.charcoal)};
`

const StepTitle = styled(Text)`
  color: ${Colors.black};
  margin-top: 0;
`

const StepList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StepListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`

const StepDescription = styled(Text)`
  color: ${Colors.charcoal};
`

const steps = [
  {
    title: 'Prepare for catch-up bookkeeping',
    description: [
      'Add any other accounts used for your business',
      'Let us know if you have personal transactions in your account(s)',
      'Upload bank statements to set up automatic reconciliation',
    ],
  },
  {
    title: 'Catch-up bookkeeping',
    description:
      "We'll handle the rest! Your books will be professionally categorized and reconciled by our team.",
  },
]

export const H1GetStartedUpdatesObScreen = () => {
  const currentUser = useReselector(getCurrentUser)

  return (
    <MainContainer>
      <WelcomeImage>
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/book-celebration.svg"
          alt=""
          style={{ width: '180px', height: 'auto' }}
        />
      </WelcomeImage>

      <WelcomeSection>
        <Text as="display2" style={{ fontWeight: 200 }}>
          Welcome to Heard
          {currentUser?.firstName ? `, ${currentUser.firstName}` : ''}!
        </Text>
        <WelcomeTextContainer>
          <Text as="bodySm">
            We&apos;re so excited you&apos;re here. At Heard, we&apos;re on a
            mission to lighten the mental load of running your practice, so you
            can focus on what you do best.
          </Text>
        </WelcomeTextContainer>
      </WelcomeSection>

      <UpNextSection>
        <Text as="display3" style={{ color: Colors.black }}>
          Up next
        </Text>

        <StyledCard>
          <GridContainer>
            <LeftColumn>
              <ImageContainer>
                <StyledImage
                  src="https://heard-images.s3.amazonaws.com/assets/books_lightShadow.png"
                  alt=""
                />
              </ImageContainer>
              <TextSection>
                <Text as="h2" style={{ color: Colors.black }}>
                  Catch-up bookkeeping
                </Text>
                <Text as="bodySm" style={{ color: Colors.charcoal }}>
                  A complete review of your books from January-April 2025.
                </Text>
              </TextSection>
              <DescriptionSection>
                <Text as="bodySm">
                  Catch-up bookkeeping is a <strong>one-time</strong> process in
                  which we set up your account and ensure your books are
                  audit-proof.
                </Text>
              </DescriptionSection>
            </LeftColumn>

            <RightColumn>
              <EyebrowText as="eyebrow">HOW IT WORKS</EyebrowText>

              <StepsContainer>
                {steps.map((step, index) => (
                  <StepCard key={step.title}>
                    <StepContent>
                      <StepHeader>
                        <StepNumber isActive={index === 0}>
                          <StepNumberText as="bodySm" isActive={index === 0}>
                            {index + 1}
                          </StepNumberText>
                        </StepNumber>
                        <StepTitle as="h3">{step.title}</StepTitle>
                      </StepHeader>
                      {Array.isArray(step.description) ? (
                        <StepList>
                          {step.description.map((item) => (
                            <StepListItem key={item}>
                              <StepDescription as="bodySm">•</StepDescription>
                              <StepDescription as="bodySm">
                                {item}
                              </StepDescription>
                            </StepListItem>
                          ))}
                        </StepList>
                      ) : (
                        <StepDescription as="bodySm">
                          {step.description}
                        </StepDescription>
                      )}
                    </StepContent>
                  </StepCard>
                ))}
              </StepsContainer>
            </RightColumn>
          </GridContainer>
        </StyledCard>
      </UpNextSection>
    </MainContainer>
  )
}
